import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type GetServerSideProps } from 'next'
import { useState } from 'react'

import AppBanner from '@/components/AppBanner'
import BANNER_TEXT_VALUES from '@/components/AppBanner/constants'
import Cobrand from '@/components/Cobrand'
import Hotel from '@/components/Hotels'
import Layout from '@/components/Layout'
import Meta from '@/components/Meta'
import SchemaMarkup from '@/components/SchemaMarkup/SchemaMarkup'
import { SearchFormAutocompleteWiring } from '@/components/SearchFormAutocompleteWiring/SearchFormAutocompleteWiring'
import TmsScript from '@/components/TmsScript/TmsScript'
import TravelChatBot from '@/components/TravelChatBot'
import HotelSchemaMarkup from '@/constants/hotel-schema-markup'
import DEFAULT_META_TAGS from '@/constants/meta'
import { HOTEL_PRODUCT_TYPE } from '@/constants/products'
import useCanonicalUrl from '@/hooks/useCanonicalUrl'
import usePageTitleForHotels from '@/hooks/usePageTitleForHotels'
import generateBootstrapData from '@/shared-utils/generate-bootstrap-data'
import mapSetiResponse from '@/shared-utils/map-seti-response'
import type { ContentPageProps, CustomContext } from '@/types'

import setiService from '../../server/middleware/seti'
import prePopulateHotelValues from '../../server/prepop/hotels'
import getContentfulData from '../../server/utils/get-contentful-data'
import getSkymondeData from '../../server/utils/get-skymonde-data'
import oktaJwtVerify from '../../server/utils/okta-jwt-verifier'
import fetchPCLNHeaderFooterContent from '../../server/utils/pcln-header-footer'
import retrieveContentfulSecrets from '../../server/utils/retrieve-contentful-secrets'

function Hotels({ content, headerHTML, footerHTML, bootstrapData }: ContentPageProps) {
  const pageTitle = usePageTitleForHotels(DEFAULT_META_TAGS.title.hotels)
  const canonicalUrl = useCanonicalUrl()
  const [queryClient] = useState(() => new QueryClient())

  return (
    <>
      <Meta
        description={DEFAULT_META_TAGS.description.hotels}
        pageTitle={pageTitle}
        canonicalUrl={canonicalUrl}
      />
      <SchemaMarkup schema={HotelSchemaMarkup} />
      <TmsScript product='hotels' />
      <FeatureProvider features={bootstrapData.featureToggles}>
        <ContentfulProvider contentful={content}>
          <QueryClientProvider client={queryClient}>
            <SearchFormAutocompleteWiring>
              <AppBanner content={BANNER_TEXT_VALUES[HOTEL_PRODUCT_TYPE]} />
              <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
                <Cobrand />
                <Hotel />
                <TravelChatBot
                  enableChatBot
                  askPrompt=''
                  openPenny=''
                  productType='hotel'
                  pageName='landing'
                />
              </Layout>
            </SearchFormAutocompleteWiring>
          </QueryClientProvider>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<ContentPageProps> = async (ctx) => {
  const { req, res } = ctx as CustomContext
  const bootstrapData = generateBootstrapData({ product: 'hotels', req, res })
  const { webstats, referral, appVersion, locale } = bootstrapData
  const { clientGUID: cguid } = webstats

  const { contentfulDeliverySecret, contentfulPreviewSecret } = retrieveContentfulSecrets()
  const isContentfulEnabled = res.locals.features?.siteExperience?.isContentfulEnabled ?? true
  const isSkymondeEnabled = res.locals.features?.siteExperience?.isSkymondeEnabled ?? false
  const [hotelPrePopData, pclnHeaderFooterContent, signInStatus, { content, experimentNames }, skymondeSEO] =
    await Promise.all([
      prePopulateHotelValues(ctx.query),
      fetchPCLNHeaderFooterContent(req, res),
      oktaJwtVerify({ authHdr: req.headers.authorization ?? '', cguid }),
      getContentfulData({
        contentTypeId: 'pageLanding',
        contentfulDeliverySecret,
        contentfulPreviewSecret,
        product: 'Hotel',
        contentPreview: ctx.query['content-preview'] === 'true',
        isContentfulEnabled,
      }),
      getSkymondeData('hotels', locale.localeCode, isSkymondeEnabled),
    ])

  const setiResponseEvents = await setiService(req, {
    appVersion,
    allowSetiOverrides: true,
    webstats,
    referral,
    reqQueryParams: ctx.query,
    ...(experimentNames && {
      additionalExperiments: experimentNames,
    }),
  })
  const prePopData = hotelPrePopData ? { hotels: hotelPrePopData } : null

  const bootstrapWithJwtSignInData = {
    ...bootstrapData,
    customer: {
      signedIn: signInStatus.signedIn ?? false,
      emailAddress: signInStatus.email ?? '',
      firstName: String(signInStatus.firstName ?? ''),
    },
  }

  return {
    props: {
      content,
      ...pclnHeaderFooterContent,
      bootstrapData: {
        ...bootstrapWithJwtSignInData,
        signInStatus,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        ...(prePopData && { prePopData }),
        skymondeSEO,
      },
    },
  }
}

export default Hotels
